import { Box, Center, HStack, Icon, Tag, Text, Button, Tooltip, Card } from '@televet/kibble-ui';
import React, { useCallback, useMemo } from 'react';
import { AppointmentFullSelectionFragment, AppointmentStatus } from 'shared/types/graphql';
import { DetailViewTabName, ShowDetailView } from '../../../shared/types/enum/Mode';
import { format } from 'date-fns';
import { ExtendedAppointment } from 'pages/Appointments';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SidePanelPetProfileEvents } from 'shared/providers/TrackingProvider/EventNames';

interface ISidePanelAppointmentsProps {
  onGetPetParentAppointment: (passedTabName?: DetailViewTabName) => AppointmentFullSelectionFragment[];
  handleAppointmentClicked: (appointment: ExtendedAppointment) => void;
  isAppointmentDataLoading: boolean;
  onShowDetailView: React.Dispatch<React.SetStateAction<ShowDetailView>>;
  showDetailView: ShowDetailView;
  currentTabName: DetailViewTabName;
  currentLoadCount: number;
  onSetCurrentLoadCount: React.Dispatch<React.SetStateAction<number>>;
  onSetTabName: React.Dispatch<React.SetStateAction<DetailViewTabName>>;
}

const SidePanelAppointments = ({
  onGetPetParentAppointment,
  handleAppointmentClicked,
  isAppointmentDataLoading,
  onShowDetailView,
  showDetailView,
  currentLoadCount,
  onSetCurrentLoadCount,
  currentTabName,
  onSetTabName,
}: ISidePanelAppointmentsProps): JSX.Element => {
  const { trackEvent } = useTracking();
  const allPetAppointments = useMemo(() => onGetPetParentAppointment(), [onGetPetParentAppointment]);

  const onAppointmentClicked = useCallback(
    (e, appointment) => {
      trackEvent(SidePanelPetProfileEvents.ViewAppointment);
      e.stopPropagation();
      handleAppointmentClicked(appointment);
    },
    [handleAppointmentClicked, trackEvent],
  );

  const onShowLoadMore = useMemo(
    () =>
      showDetailView !== ShowDetailView.None &&
      allPetAppointments?.length > 20 &&
      currentLoadCount < allPetAppointments?.length,
    [allPetAppointments?.length, currentLoadCount, showDetailView],
  );
  return allPetAppointments?.length > 0 ? (
    <Box className="SidePanelAppointments">
      {allPetAppointments?.slice(0, currentLoadCount).map((appointment) => {
        return (
          <Card
            key={appointment.id}
            my={3}
            isInteractive
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>): void => onAppointmentClicked(e, appointment)}
          >
            <HStack justify="space-between" mb={2}>
              <Text size="sm" fontWeight="bold">
                {appointment?.appointmentType?.name}
              </Text>
              <Text size="xs" variant="accent">
                {format(new Date(appointment.startAt), "MM/dd/yy '·' h:mm aaaaa'm'")}
              </Text>
            </HStack>
            {((!!appointment.status && appointment.status === AppointmentStatus.Confirmed) ||
              appointment.clinicEmployees.length > 0) && (
              <HStack justify="space-between" mb={2}>
                {appointment.clinicEmployees.length > 0 && (
                  <Tooltip label="Provider" placement="top">
                    <Center>
                      <Icon name="person" size="sm" />
                      <Text size="xs" fontWeight="bold" ml={2}>
                        {appointment.clinicEmployees[0].firstName} {appointment.clinicEmployees[0].lastName}
                      </Text>
                    </Center>
                  </Tooltip>
                )}
                {!!appointment.status && appointment.status === AppointmentStatus.Confirmed && (
                  <Tag
                    label={AppointmentStatus.Confirmed}
                    size="sm"
                    background="background.success"
                    color="text.onContrast"
                  />
                )}
              </HStack>
            )}
            {!!appointment.description && (
              <Text width="100%" variant="subtle" size="sm" mb={2}>
                {appointment.description || ''}
              </Text>
            )}
            <Tooltip label="Appointment Duration" placement="top">
              <HStack width="max-content">
                <Icon name="clock" size="sm" />
                <Text size="xs" fontWeight="bold">
                  {appointment.durationInMinutes} minutes
                </Text>
              </HStack>
            </Tooltip>
          </Card>
        );
      })}
      {onShowLoadMore && (
        <Button
          width="100%"
          variant="tertiary"
          size="sm"
          onClick={(): void => onSetCurrentLoadCount((prev) => prev + 20)}
        >
          Load More
        </Button>
      )}
      {showDetailView === ShowDetailView.None && (
        <Button
          width="100%"
          variant="tertiary"
          size="sm"
          onClick={(): void => {
            onShowDetailView(ShowDetailView.Appointments);
            onSetTabName(DetailViewTabName.Upcoming);
            onSetCurrentLoadCount(20);
          }}
        >
          View Upcoming Appointments
        </Button>
      )}
    </Box>
  ) : (
    <Center my={3} className="SidePanelAppointments">
      <Text variant="subtle">
        {isAppointmentDataLoading
          ? 'Loading...'
          : currentTabName === DetailViewTabName.History
          ? 'No appointment history'
          : 'No upcoming appointments'}
      </Text>
    </Center>
  );
};

export default SidePanelAppointments;
